/* This file contains logic that spans multiple branches of the state tree.
 * It should be used in Components' mapStateToProps functions, so that our core
 * logic is completely testable.
 */

import { riskAssessmentBasePrice } from '@avalara/bat-apps-shared/constants';
import { formatPrice } from '../../shared/helpers';
import { getTotalCost as getAvataxCost, getCartTotalCost as getAvataxCostStr } from '../lib/buy-flow';
import { selectReturnsPricingData } from '../selectors/returns';
import { getSalestaxDisplayText } from './helpers';

import type { Contact, OfferInfo, ReturnsOfferInfo, RiskAssessmentOfferInfo } from '@avalara/types/index';
import type { State } from '@avalara/types/reducer-states';

export const getRiskAssessmentPricingData = (state: State) => {
  const { discountPercent, discountName } = state.order;

  const salestax = typeof state.riskAssessment.salestax === 'number' ? state.riskAssessment.salestax : 0;

  const discountAmount = discountPercent ? (riskAssessmentBasePrice * discountPercent) / 100 : 0;

  const subTotal = riskAssessmentBasePrice - discountAmount;
  const totalPrice = subTotal + salestax;
  const salestaxDisplayText = getSalestaxDisplayText(subTotal === 0 ? 0 : state.riskAssessment.salestax);

  return {
    basePrice: riskAssessmentBasePrice,
    salestax,
    salestaxDisplayText,
    subTotal,
    totalPrice,
    discountPercent,
    discountAmount: discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    discountName,
  };
};

// TODO (Andrew) - move this check to the server, we shouldn't be trusting the
//                 client to set the offer id, since we sell things that not all
//                 users are eligible for
// Given the user's active signup flow (e.g. free trial), return the appropriate offer
// based on redux state
export const getOfferByActiveFlow = (state: State, paymentToken: string | null | undefined): OfferInfo => {
  // This switch must contain all the unpaid flows where the paymentToken is optional:
  switch (state.system.activeFlow) {
    case 'esb-trial':
      return {
        id: 'esb', // NOTE: Using generic offer code here; esb offer codes are handled on server
        config: { paymentMethodId: paymentToken },
      };
    case 'risk-assessment-net-new': {
      const { totalPrice: riskAssessmentTotalPrice } = getRiskAssessmentPricingData(state);
      const riskAssessment: RiskAssessmentOfferInfo = {
        id: 'nexusstudyonline',
        config: {
          paymentMethodId: paymentToken,
          totalCost: formatPrice(riskAssessmentTotalPrice),
        },
      };
      return riskAssessment;
    }
    default:
      // Not in an unpaid flow if none of the above cases matched:
      if (paymentToken == null) throw new Error('No valid payment found');
  }

  // This switch contains all the flows where the paymentToken is required:
  switch (state.system.activeFlow) {
    case 'avatax-buy':
    case 'avatax-direct-buy': {
      const connectorId =
        state.system.activeFlow === 'avatax-direct-buy'
          ? state.avataxSignup.selectedConnector?.id || 'INVALID_SIGNUP'
          : state.account.connector.id;

      return {
        id: 'avataxtieredplan',
        config: {
          tierNumber: state.buyFlow.selectedTier?.tierNumber || 1,
          connectorId,
          paymentMethodId: paymentToken,
        },
      };
    }
    case 'returns': {
      const { totalPrice } = selectReturnsPricingData(state);
      const returnsOffer: ReturnsOfferInfo = {
        // Note (Andrew) - Validation for the returns flow is currently done server-side,
        // but return a valid offer here to avoid flow errors, even though
        // we don't use (and should not use) these values
        id: 'default-perunit-mrs',
        config: {
          paymentMethodId: paymentToken,
          totalCost: totalPrice,
        },
      };
      return returnsOffer;
    }
    case 'risk-assessment': {
      const { totalPrice: riskAssessmentTotalPrice } = getRiskAssessmentPricingData(state);
      const riskAssessment: RiskAssessmentOfferInfo = {
        id: 'nexusstudyonline',
        config: {
          paymentMethodId: paymentToken,
          totalCost: formatPrice(riskAssessmentTotalPrice),
        },
      };
      return riskAssessment;
    }
    default:
      throw new Error('Not valid signup');
  }
};

// Used for sending a confirmation email to customers
export const getOfferMetadataByActiveFlow = (state: State) => {
  switch (state.system.activeFlow) {
    case 'avatax-buy':
    case 'avatax-direct-buy':
      return { totalCost: getAvataxCostStr(state.buyFlow), salestax: state.buyFlow.salestax };
    default:
      return undefined;
  }
};

export const getAnalyticsByActiveFlow = (state: State): { eventName: string; data: unknown } => {
  const {
    abTesting,
    account,
    avataxSignup,
    buyFlow,
    system: { activeFlow },
  } = state;

  const commonData = {
    eventType: 'PURCHASE_SUCCESS',
    flow: activeFlow,
  };

  switch (activeFlow) {
    case 'avatax-direct-buy':
      return {
        eventName: 'CREATE_PAID_AVATAX_ACCOUNT_SUCCESS',
        data: {
          ...commonData,
          purchaseType: 'NET_NEW',
          abBucket: abTesting.group,
          accountId: account.accountId,
          connector: avataxSignup.selectedConnector?.name,
          price: getAvataxCost(buyFlow),
          tierNumber: buyFlow.selectedTier?.tierNumber,
          level: avataxSignup.selectedConnector?.level || '0',
        },
      };
    case 'avatax-buy':
      return {
        eventName: 'ADD_PAID_AVATAX_TIER_SUCCESS',
        data: {
          ...commonData,
          purchaseType: 'ADD',
          abBucket: abTesting.group,
          accountId: account.accountId,
          connector: account.connector.name,
          price: getAvataxCost(buyFlow),
          tierNumber: buyFlow.selectedTier?.tierNumber,
          level: account.connector.level != null ? account.connector.level : '0',
        },
      };
    case 'returns':
      return {
        eventName: 'ADD_RETURNS_SUCCESS',
        data: {
          ...commonData,
          purchaseType: 'ADD',
          abBucket: abTesting.group,
          accountId: account.accountId,
          offerId: 'default-perunit-mrs',
          price: selectReturnsPricingData(state).total,
        },
      };
    case 'esb-trial':
      return {
        eventName: 'ESB_SIGNUP_SUCCESS',
        data: {
          ...commonData,
          purchaseType: 'NET_NEW',
          abBucket: abTesting.group,
          accountId: avataxSignup.accountId,
        },
      };
    case 'risk-assessment':
      return {
        eventName: 'ADD_RISK_ASSESSMENT_SUCCESS',
        data: {
          ...commonData,
          purchaseType: 'ADD',
          abBucket: abTesting.group,
          accountId: account.accountId,
          offerId: 'nexusstudyonline',
          price: getRiskAssessmentPricingData(state).totalPrice,
        },
      };
    case 'risk-assessment-net-new':
      return {
        eventName: 'NET_NEW_RISK_ASSESSMENT_SUCCESS',
        data: {
          ...commonData,
          purchaseType: 'NET_NEW',
          abBucket: abTesting.group,
          accountId: avataxSignup.accountId,
          offerId: 'nexusstudyonline',
          price: getRiskAssessmentPricingData(state).totalPrice,
        },
      };
    default:
      throw new Error('no active flow!');
  }
};

export const getContact = (reduxState: State): Contact | 'ERROR' | null => {
  const {
    account: { billingContact },
    sharedSignup: { signupForm },
  } = reduxState;

  if (billingContact === 'ERROR') return 'ERROR';

  const {
    email,
    firstName,
    lastName,
    street,
    state,
    phone: phoneNumber,
    postalCode,
    city,
  } = billingContact || {
    ...signupForm,
    street: [signupForm.street1, signupForm.street2].filter(Boolean).join(' '),
  };

  // container mounted without this information yet
  if (postalCode == null || state == null || !street) return null;

  return {
    email,
    firstName,
    lastName,
    phoneNumber,
    title: null,
    street,
    state,
    postalCode,
    city,
    country: 'US',
  };
};
